import React, { useState, useEffect, useMemo } from "react";
import { useTableColumnFilters, FilterTh } from "../components/tableFilters";
import { useNavigate } from "react-router-dom";
import { CostCategory, CostItem } from "../types";
import { costService } from "../services/dataService";
import { formatCurrency } from "../utils/formatters";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import "./CostManagement.css";
import { usePermissions } from "../context/PermissionContext";

interface CostManagementProps {}

const CostManagement: React.FC<CostManagementProps> = () => {
  const { has, hasModule } = usePermissions(); // yetki matrisi
  const [activeTab, setActiveTab] = useState<"categories" | "items">(
    "categories",
  );
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<CostCategory[]>([]);
  const [costItems, setCostItems] = useState<CostItem[]>([]);
  const [selectedCategoryFilter, setSelectedCategoryFilter] =
    useState<string>("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<CostItem | null>(null);
  const [editCategoryModalOpen, setEditCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CostCategory | null>(
    null,
  );
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(),
  );

  // Alert Modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  // Confirm Modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "danger";
    onConfirm: () => void;
  }>({ isOpen: false, message: "", type: "warning", onConfirm: () => {} });

  const navigate = useNavigate();

  useEffect(() => {
    if (activeTab === "categories") {
      loadCategories();
      loadCostItems(); // Kategoriler için de maliyet kalemlerini yükle
    } else if (activeTab === "items") {
      loadCostItems();
      loadCategories(); // Kategori filtresi için kategorileri de yükle
    }
  }, [activeTab]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await costService.getCostCategories();
      setCategories(data);
    } catch (error) {
      console.error("❌ Failed to load categories:", error);
      setAlertModal({
        isOpen: true,
        message: "Kategoriler yüklenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadCostItems = async () => {
    try {
      setLoading(true);
      const data = await costService.getCostItems();
      setCostItems(data);
    } catch (error) {
      console.error("❌ Failed to load cost items:", error);
      setAlertModal({
        isOpen: true,
        message: "Maliyet kalemleri yüklenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("tr-TR");
  };

  // Kategori adını bul
  const getCategoryName = (categoryId: string): string => {
    const category = categories.find((c) => c.costCategoryId === categoryId);
    return category?.categoryName || "-";
  };

  const handleEditItem = (item: CostItem) => {
    setEditingItem(item);
    setEditModalOpen(true);
  };

  const handleSaveEdit = async (updatedItem: CostItem) => {
    if (!editingItem) return;

    try {
      setLoading(true);
      await costService.updateCostItem(editingItem.costItemId, updatedItem);
      setAlertModal({
        isOpen: true,
        message: "Maliyet kalemi başarıyla güncellendi!",
        type: "success",
      });
      setEditModalOpen(false);
      setEditingItem(null);
      loadCostItems();
    } catch (error) {
      console.error("Failed to update cost item:", error);
      setAlertModal({
        isOpen: true,
        message: "Maliyet kalemi güncellenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditCategory = (category: CostCategory) => {
    setEditingCategory(category);
    setEditCategoryModalOpen(true);
  };

  const handleSaveCategoryEdit = async (updatedCategory: CostCategory) => {
    if (!editingCategory) return;

    try {
      setLoading(true);
      await costService.updateCostCategory(
        editingCategory.costCategoryId,
        updatedCategory,
      );
      setAlertModal({
        isOpen: true,
        message: "Kategori başarıyla güncellendi!",
        type: "success",
      });
      setEditCategoryModalOpen(false);
      setEditingCategory(null);
      loadCategories();
    } catch (error) {
      console.error("Failed to update category:", error);
      setAlertModal({
        isOpen: true,
        message: "Kategori güncellenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (
    categoryId: string,
    categoryName: string,
  ) => {
    setConfirmModal({
      isOpen: true,
      message: `"${categoryName}" kategorisini silmek istediğinize emin misiniz?\n\nBu kategoriye ait maliyet kalemleri de silinecektir!`,
      type: "danger",
      onConfirm: async () => {
        try {
          setLoading(true);
          await costService.deleteCostCategory(categoryId);
          setAlertModal({
            isOpen: true,
            message: "Kategori başarıyla silindi!",
            type: "success",
          });
          loadCategories();
          loadCostItems();
        } catch (error) {
          console.error("Failed to delete category:", error);
          setAlertModal({
            isOpen: true,
            message: "Kategori silinemedi! Bu kategori kullanımda olabilir.",
            type: "error",
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Maliyet kalemi silme helper fonksiyonu
  const handleDeleteCostItem = (itemId: string, itemName: string) => {
    setConfirmModal({
      isOpen: true,
      message: `"${itemName}" maliyet kalemini silmek istediğinize emin misiniz?\n\nBu işlem geri alınamaz!`,
      type: "danger",
      onConfirm: async () => {
        try {
          await costService.deleteCostItem(itemId);
          setAlertModal({
            isOpen: true,
            message: "Maliyet kalemi başarıyla silindi!",
            type: "success",
          });
          loadCostItems();
        } catch (error) {
          console.error("Failed to delete cost item:", error);
          setAlertModal({
            isOpen: true,
            message: "Maliyet kalemi silinemedi! Bu kalem kullanımda olabilir.",
            type: "error",
          });
        }
      },
    });
  };

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const getCategoryItems = (categoryId: string) => {
    return costItems.filter((item) => item.costCategoryId === categoryId);
  };

  const cmBaseItems = useMemo(
    () => costItems.filter((item) => (selectedCategoryFilter ? item.costCategoryId === selectedCategoryFilter : true)),
    [costItems, selectedCategoryFilter],
  );
  const cmItemDefs = useMemo(
    () => [
      { key: "name", value: (i: any) => i.itemName || "" },
      { key: "category", value: (i: any) => i.costCategory?.categoryName || getCategoryName(i.costCategoryId) },
      { key: "price", type: "decimal", value: (i: any) => i.unitPrice ?? null },
      { key: "currency", value: (i: any) => i.currency || "" },
      { key: "wastage", type: "decimal", value: (i: any) => i.wastagePercentage ?? null },
      { key: "supplier", value: (i: any) => i.supplier || "" },
      { key: "updated", type: "datetime", value: (i: any) => i.lastPriceUpdate || null },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const { ctx: cmCf, filtered: cmItems } = useTableColumnFilters(cmBaseItems, cmItemDefs);

  if (loading) {
    return <PageLoader message="Maliyet verileri yükleniyor..." />;
  }

  return (
    <div className="cost-management-container">
      <div className="cost-management-header">
        <div className="cm-breadcrumb">
          <span>Yönetim</span>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6" />
          </svg>
          <span className="active">Maliyet Yönetimi</span>
        </div>
        <div className="cm-head-main">
          <div className="cm-head-left">
            <div className="cm-head-icon">
              <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
            </div>
            <div>
              <h1>Maliyet Yönetimi</h1>
              <p className="header-subtitle">
                Kategori, kalem ve fire tanımlamaları
              </p>
            </div>
          </div>
        </div>
        <div className="tab-navigation">
          <button
            className={`tab-button ${
              activeTab === "categories" ? "active" : ""
            }`}
            onClick={() => setActiveTab("categories")}
          >
            Kategoriler
          </button>
          <button
            className={`tab-button ${activeTab === "items" ? "active" : ""}`}
            onClick={() => setActiveTab("items")}
          >
            Maliyet Kalemleri
          </button>
        </div>
      </div>

      <div className="tab-content">
        {activeTab === "categories" && (
          <div className="categories-section">
            <div className="section-header">
              <h2>Maliyet Kategorileri</h2>
              {has("cost:categories:create") && (
              <button
                className="btn btn-primary"
                onClick={() => navigate("/cost/categories/new")}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z" />
                </svg>
                Yeni Kategori
              </button>
              )}
            </div>

            <div className="categories-grid">
              {categories.map((category) => {
                const categoryItems = getCategoryItems(category.costCategoryId);
                const isExpanded = expandedCategories.has(
                  category.costCategoryId,
                );

                return (
                  <div
                    key={category.costCategoryId}
                    className="category-card-expanded"
                  >
                    <div className="category-header-row">
                      <div className="category-info">
                        <h3>{category.categoryName}</h3>
                        {category.description && (
                          <p className="category-description">
                            {category.description}
                          </p>
                        )}
                        <span className="category-item-count">
                          {categoryItems.length} maliyet kalemi
                        </span>
                      </div>
                      <div className="category-actions-row">
                        {has("cost:categories:update") && (
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleEditCategory(category)}
                          title="Düzenle"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                          Düzenle
                        </button>
                        )}
                        {has("cost:categories:delete") && (
                        <button
                          className="btn btn-sm btn-soft-danger"
                          onClick={() =>
                            handleDeleteCategory(
                              category.costCategoryId,
                              category.categoryName,
                            )
                          }
                          title="Sil"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            <line x1="10" y1="11" x2="10" y2="17" />
                            <line x1="14" y1="11" x2="14" y2="17" />
                          </svg>
                          Sil
                        </button>
                        )}
                        <button
                          className={`expand-btn ${
                            isExpanded ? "expanded" : ""
                          }`}
                          onClick={() =>
                            toggleCategory(category.costCategoryId)
                          }
                          title={isExpanded ? "Gizle" : "Göster"}
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {isExpanded && categoryItems.length > 0 && (
                      <div className="category-items-list">
                        <div className="items-list-header">
                          <h4>Maliyet Kalemleri</h4>
                        </div>
                        <table className="category-items-table rt rt-stack">
                          <thead>
                            <tr>
                              <th>Kalem Adı</th>
                              <th>Birim Fiyat</th>
                              <th className="rt-p4">Döviz</th>
                              <th className="rt-p3">Fire %</th>
                              <th className="rt-p3">Tedarikçi</th>
                              <th>İşlemler</th>
                            </tr>
                          </thead>
                          <tbody>
                            {categoryItems.map((item) => (
                              <tr key={item.costItemId}>
                                <td className="rt-main">
                                  <div className="item-name">
                                    <strong>{item.itemName}</strong>
                                    {item.description && (
                                      <span className="item-description">
                                        {item.description}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="price-cell" data-label="Birim Fiyat">
                                  {formatCurrency(
                                    item.unitPrice,
                                    item.currency,
                                  )}
                                  {item.discountPercentage && (
                                    <span className="discount-badge">
                                      -%{item.discountPercentage}
                                    </span>
                                  )}
                                </td>
                                <td className="rt-p4" data-label="Döviz">
                                  <span className="currency-badge">
                                    {item.currency}
                                  </span>
                                </td>
                                <td className="rt-p3" data-label="Fire">
                                  {item.wastagePercentage ? (
                                    <span
                                      style={{
                                        color: "#ff9800",
                                        fontWeight: "500",
                                      }}
                                    >
                                      %{item.wastagePercentage}
                                    </span>
                                  ) : (
                                    <span style={{ color: "#ccc" }}>-</span>
                                  )}
                                </td>
                                <td className="rt-p3" data-label="Tedarikçi">{item.supplier || "-"}</td>
                                <td className="rt-actions">
                                  <div className="action-buttons">
                                    {has("cost:items:update") && (
                                    <button
                                      className="btn btn-sm btn-secondary"
                                      onClick={() => handleEditItem(item)}
                                      title="Düzenle"
                                    >
                                      <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                      >
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                      </svg>
                                    </button>
                                    )}
                                    {has("cost:items:delete") && (
                                    <button
                                      className="btn btn-sm btn-soft-danger"
                                      onClick={() =>
                                        handleDeleteCostItem(
                                          item.costItemId,
                                          item.itemName,
                                        )
                                      }
                                      title="Sil"
                                    >
                                      <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                      >
                                        <polyline points="3 6 5 6 21 6" />
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                        <line x1="10" y1="11" x2="10" y2="17" />
                                        <line x1="14" y1="11" x2="14" y2="17" />
                                      </svg>
                                    </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {isExpanded && categoryItems.length === 0 && (
                      <div className="no-items-message">
                        Bu kategoriye ait maliyet kalemi bulunmuyor.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === "items" && (
          <div className="items-section">
            <div className="section-header">
              <h2>Maliyet Kalemleri</h2>
              <div className="section-header-actions">
                <div className="category-filter">
                  <label>Kategori:</label>
                  <select
                    value={selectedCategoryFilter}
                    onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                  >
                    <option value="">Tümü</option>
                    {categories.map((cat) => (
                      <option
                        key={cat.costCategoryId}
                        value={cat.costCategoryId}
                      >
                        {cat.categoryName}
                      </option>
                    ))}
                  </select>
                </div>
                {has("cost:items:create") && (
                <button
                  className="btn btn-primary"
                  onClick={() => navigate("/cost/items/new")}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z" />
                  </svg>
                  Yeni Maliyet Kalemi
                </button>
                )}
              </div>
            </div>

            <div className="items-table-container">
              <table className="items-table rt rt-stack">
                <thead>
                  <tr>
                    <FilterTh label="Kalem Adı" colKey="name" ctx={cmCf} />
                    <FilterTh label="Kategori" colKey="category" ctx={cmCf} />
                    <FilterTh label="Birim Fiyat" colKey="price" type="decimal" ctx={cmCf} align="left" />
                    <FilterTh label="Döviz" colKey="currency" ctx={cmCf} className="rt-p4" />
                    <FilterTh label="Fire %" colKey="wastage" type="decimal" ctx={cmCf} align="left" className="rt-p3" />
                    <FilterTh label="Tedarikçi" colKey="supplier" ctx={cmCf} className="rt-p3" />
                    <FilterTh label="Son Güncelleme" colKey="updated" type="datetime" ctx={cmCf} className="rt-p2" />
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {cmItems
                    .map((item) => {
                      return (
                        <tr key={item.costItemId}>
                          <td className="rt-main">
                            <div className="item-name">
                              <strong>{item.itemName}</strong>
                              {item.description && (
                                <span className="item-description">
                                  {item.description}
                                </span>
                              )}
                            </div>
                          </td>
                          <td data-label="Kategori">
                            <span className="category-badge">
                              {item.costCategory?.categoryName ||
                                getCategoryName(item.costCategoryId)}
                            </span>
                          </td>
                          <td className="price-cell" data-label="Birim Fiyat">
                            {formatCurrency(item.unitPrice, item.currency)}
                            {item.discountPercentage && (
                              <span className="discount-badge">
                                -%{item.discountPercentage}
                              </span>
                            )}
                          </td>
                          <td className="rt-p4" data-label="Döviz">
                            <span className="currency-badge">
                              {item.currency}
                            </span>
                          </td>
                          <td className="rt-p3" data-label="Fire">
                            {item.wastagePercentage ? (
                              <span
                                style={{ color: "#ff9800", fontWeight: "500" }}
                              >
                                %{item.wastagePercentage}
                              </span>
                            ) : (
                              <span style={{ color: "#ccc" }}>-</span>
                            )}
                          </td>
                          <td className="rt-p3" data-label="Tedarikçi">{item.supplier || "-"}</td>
                          <td className="rt-p2" data-label="Güncelleme">{formatDate(item.lastPriceUpdate)}</td>
                          <td className="rt-actions">
                            <div className="action-buttons">
                              {has("cost:items:update") && (
                              <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => handleEditItem(item)}
                                title="Düzenle"
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                                Düzenle
                              </button>
                              )}

                              {has("cost:items:delete") && (
                              <button
                                className="btn btn-sm btn-soft-danger"
                                onClick={() =>
                                  handleDeleteCostItem(
                                    item.costItemId,
                                    item.itemName,
                                  )
                                }
                                title="Sil"
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                  <line x1="10" y1="11" x2="10" y2="17" />
                                  <line x1="14" y1="11" x2="14" y2="17" />
                                </svg>
                                Sil
                              </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>

      {/* Edit Modal */}
      {editModalOpen && editingItem && (
        <EditCostItemModal
          item={editingItem}
          categories={categories}
          onClose={() => {
            setEditModalOpen(false);
            setEditingItem(null);
          }}
          onSave={handleSaveEdit}
        />
      )}

      {/* Edit Category Modal */}
      {editCategoryModalOpen && editingCategory && (
        <EditCategoryModal
          category={editingCategory}
          onClose={() => {
            setEditCategoryModalOpen(false);
            setEditingCategory(null);
          }}
          onSave={handleSaveCategoryEdit}
        />
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={() => {
          confirmModal.onConfirm();
          setConfirmModal({ ...confirmModal, isOpen: false });
        }}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </div>
  );
};

interface EditCostItemModalProps {
  item: CostItem;
  categories: CostCategory[];
  onClose: () => void;
  onSave: (item: CostItem) => void;
}

const EditCostItemModal: React.FC<EditCostItemModalProps> = ({
  item,
  categories,
  onClose,
  onSave,
}) => {
  const { hasModule } = usePermissions();
  const [formData, setFormData] = useState<CostItem>({ ...item });
  // Stok bağı bu kısa formdan kaldırıldı (kalem detay ekranında, çok satırlı);
  // eskiden burada stok listesi yükleniyordu, artık gerek yok.

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Maliyet Kalemini Düzenle</h2>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-group">
            <label>Kalem Adı *</label>
            <input
              type="text"
              value={formData.itemName}
              onChange={(e) =>
                setFormData({ ...formData, itemName: e.target.value })
              }
              required
            />
          </div>

          {/* Stok düşümü artık çok satırlı (bir kalem birden fazla malzeme tüketebilir).
              Bu kısa formda tekrar etmek yerine kalem detay ekranına yönlendiriyoruz. */}
          {hasModule("stock") && (<div className="form-group">
            <label>Stok düşümü</label>
            <p className="form-hint" style={{ margin: 0 }}>
              Bu kalemin hangi stoklardan ne kadar düşeceği kalem detay ekranından
              tanımlanır — bir kaleme birden fazla malzeme bağlanabilir.
            </p>
          </div>)}

          <div className="form-row">
            <div className="form-group">
              <label>Kategori *</label>
              <select
                value={formData.costCategoryId}
                onChange={(e) =>
                  setFormData({ ...formData, costCategoryId: e.target.value })
                }
                required
              >
                <option value="">Seçiniz</option>
                {categories.map((cat) => (
                  <option key={cat.costCategoryId} value={cat.costCategoryId}>
                    {cat.categoryName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Birim Fiyat *</label>
              <input
                type="number"
                step="0.01"
                value={formData.unitPrice}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    unitPrice: parseFloat(e.target.value),
                  })
                }
                required
              />
            </div>

            <div className="form-group">
              <label>Döviz *</label>
              <select
                value={formData.currency}
                onChange={(e) =>
                  setFormData({ ...formData, currency: e.target.value })
                }
                required
              >
                <option value="TRY">TRY</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Tedarikçi</label>
              <input
                type="text"
                value={formData.supplier || ""}
                onChange={(e) =>
                  setFormData({ ...formData, supplier: e.target.value })
                }
              />
            </div>

            <div className="form-group">
              <label>Fire %</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={formData.wastagePercentage || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    wastagePercentage: parseFloat(e.target.value) || undefined,
                  })
                }
              />
            </div>
          </div>

          <div className="form-group">
            <label>Açıklama</label>
            <textarea
              value={formData.description || ""}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows={3}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>1. Miktar Etiketi</label>
              <input
                type="text"
                value={formData.quantity1Label || ""}
                onChange={(e) =>
                  setFormData({ ...formData, quantity1Label: e.target.value })
                }
                placeholder="Örn: En, Miktar, Adet"
              />
            </div>

            {formData.quantity2Label && (
              <div className="form-group">
                <label>2. Miktar Etiketi</label>
                <input
                  type="text"
                  value={formData.quantity2Label || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, quantity2Label: e.target.value })
                  }
                  placeholder="Örn: Boy, Çeşit Sayısı"
                />
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              İptal
            </button>
            <button type="submit" className="btn btn-primary">
              Kaydet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface EditCategoryModalProps {
  category: CostCategory;
  onClose: () => void;
  onSave: (category: CostCategory) => void;
}

const EditCategoryModal: React.FC<EditCategoryModalProps> = ({
  category,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<CostCategory>({ ...category });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Kategoriyi Düzenle</h2>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-group">
            <label>Kategori Adı *</label>
            <input
              type="text"
              value={formData.categoryName}
              onChange={(e) =>
                setFormData({ ...formData, categoryName: e.target.value })
              }
              required
            />
          </div>

          <div className="form-group">
            <label>Açıklama</label>
            <textarea
              value={formData.description || ""}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows={3}
            />
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              İptal
            </button>
            <button type="submit" className="btn btn-primary">
              Kaydet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Ana component'ın JSX'inin sonuna alert ve confirm modal ekle
// Bu, CostManagement component'ının içinde olmalı

export default CostManagement;
